import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, FileText, FileSpreadsheet, Loader2,
  Package, DollarSign, Wrench, ShoppingCart, History, Clock, UserCircle, AlertTriangle, Eye,
} from "lucide-react";
import { PriceDiffDialog } from "@/components/PriceDiffDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import Layout from "@/components/Layout";
import SuministroTab from "@/components/quote/SuministroTab";
import SummaryCard from "@/components/quote/SummaryCard";
import MantenimientoTab from "@/components/quote/MantenimientoTab";
import DolaresTab from "@/components/quote/DolaresTab";
import SolesTab from "@/components/quote/SolesTab";
import DocumentTab from "@/components/quote/DocumentTab";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";

const CLIENT_FIELDS = [
  ["proyecto", "Nombre del proyecto"], ["codigo", "Código"], ["cliente", "Cliente"], ["contacto", "Contacto"],
  ["ciudad", "Ciudad"], ["telefono", "Teléfono"], ["direccion", "Dirección"], ["email", "Email"], ["tipo_pago", "T. Pago"],
];

export default function QuoteEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [quote, setQuote] = useState(null);
  const [computed, setComputed] = useState(null);
  const [summary, setSummary] = useState({});
  const [characteristics, setCharacteristics] = useState([]);
  const [tab, setTab] = useState("suministro");
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [versions, setVersions] = useState([]);
  const [histOpen, setHistOpen] = useState(false);
  const [references, setReferences] = useState(null);
  const [priceDiff, setPriceDiff] = useState(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const debTimer = useRef(null);

  const loadPriceDiff = useCallback(() => api.get(`/quotes/${id}/price-diff`)
    .then((r) => setPriceDiff(r.data)).catch(() => setPriceDiff(null)), [id]);
  useEffect(() => { loadPriceDiff(); }, [loadPriceDiff]);
  const changedDoors = useMemo(() => Object.fromEntries((priceDiff?.doors || []).map((d) => [d.door_id, d.features])), [priceDiff]);
  const onPricesUpdated = async () => { await loadQuote(); await loadPriceDiff(); setDirty(false); };

  const loadQuote = useCallback(() => {
    return api.get(`/quotes/${id}`).then(({ data }) => {
      setQuote(data.quote);
      setComputed(data.computed);
      setSummary(data.summary || {});
      setCharacteristics(data.characteristics || []);
    });
  }, [id]);

  const loadVersions = () => api.get(`/quotes/${id}/versions`).then((r) => setVersions(r.data)).catch(() => {});

  const restore = async (versionId) => {
    if (!window.confirm("¿Restaurar la cotización a esta versión?")) return;
    try {
      await api.post(`/quotes/${id}/restore`, { version_id: versionId });
      await loadQuote();
      await loadVersions();
      setHistOpen(false);
      toast.success("Versión restaurada");
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    api.get(`/quotes/${id}`).then(({ data }) => {
      setQuote(data.quote);
      setComputed(data.computed);
      setSummary(data.summary || {});
      setCharacteristics(data.characteristics || []);
    }).catch((e) => { toast.error(apiErr(e)); nav("/"); });
    api.get("/settings/references").then((r) => setReferences(r.data)).catch(() => {});
  }, [id, nav]);

  const recompute = useCallback((q) => {
    setRecomputing(true);
    api.post("/quotes/preview", { ...q, quote_id: id })
      .then(({ data }) => {
        setComputed((prev) => {
          const next = { ...prev, soles: data.soles };
          if (data.costos) next.costos = data.costos;
          if (data.dolares) next.dolares = data.dolares;
          return next;
        });
        setSummary(data.summary || {});
      })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setRecomputing(false));
  }, [id]);

  const mutate = useCallback((updater) => {
    setQuote((prev) => {
      const next = updater({ ...prev });
      setDirty(true);
      clearTimeout(debTimer.current);
      debTimer.current = setTimeout(() => recompute(next), 450);
      return next;
    });
  }, [recompute]);

  const setField = (k, v) => mutate((q) => ({ ...q, [k]: v }));
  const setClient = (k, v) => mutate((q) => ({ ...q, client: { ...q.client, [k]: v } }));
  const onImport = (k, v) => mutate((q) => ({ ...q, import_params: { ...(q.import_params || {}), [k]: v } }));
  const onDollar = (k, v) => mutate((q) => ({ ...q, dollar_params: { ...q.dollar_params, [k]: v } }));
  const onSolesParam = (k, v) => mutate((q) => ({ ...q, soles_params: { ...q.soles_params, [k]: v } }));
  const onExport = (k, v) => mutate((q) => ({ ...q, export_config: { ...(q.export_config || {}), [k]: v } }));
  const onSectionToggle = (k, v) => mutate((q) => ({
    ...q, export_config: { ...(q.export_config || {}), sections: { ...((q.export_config || {}).sections || {}), [k]: v } },
  }));
  const onDoor = (did, f, v) => mutate((q) => ({ ...q, doors: q.doors.map((d) => d.id === did ? { ...d, [f]: v } : d) }));
  const onDoorPatch = (did, patch) => mutate((q) => ({ ...q, doors: q.doors.map((d) => d.id === did ? { ...d, ...patch } : d) }));
  const onLine = (did, f, v) => mutate((q) => ({ ...q, door_lines: (q.door_lines || []).map((l) => l.door_id === did ? { ...l, [f]: v } : l) }));
  const onLineAll = (f, v) => mutate((q) => ({ ...q, door_lines: (q.door_lines || []).map((l) => ({ ...l, [f]: v })) }));
  const onRemoveDoor = (did) => mutate((q) => ({
    ...q, doors: q.doors.filter((d) => d.id !== did),
    door_lines: (q.door_lines || []).filter((l) => l.door_id !== did),
  }));
  const onAddDoor = () => mutate((q) => {
    const nid = `new-${Date.now()}`;
    return {
      ...q,
      doors: [...(q.doors || []), { id: nid, code: "", width: 0.9, height: 2.1, qty: 1, fob_mode: "auto", fob_features: [] }],
      door_lines: [...(q.door_lines || []), { door_id: nid, utilidad: 0.32, instalacion_base: 80, maquillaje: 40, maquillaje_material: 5, otros: 0, cerradura: 0 }],
    };
  });
  const onDuplicateDoor = (did) => mutate((q) => {
    const src = (q.doors || []).find((d) => d.id === did);
    if (!src) return q;
    const nid = `new-${Date.now()}`;
    const idx = q.doors.findIndex((d) => d.id === did);
    const doors = [...q.doors];
    doors.splice(idx + 1, 0, { ...src, id: nid, code: src.code ? `${src.code} (copia)` : "" });
    const srcLine = (q.door_lines || []).find((l) => l.door_id === did)
      || { utilidad: 0.32, instalacion_base: 80, maquillaje: 40, maquillaje_material: 5, otros: 0, cerradura: 0 };
    return { ...q, doors, door_lines: [...(q.door_lines || []), { ...srcLine, door_id: nid }] };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/quotes/${id}`, {
        name: quote.name, client: quote.client, doors: quote.doors,
        import_params: quote.import_params || {}, dollar_params: quote.dollar_params || {},
        door_lines: quote.door_lines || [], soles_params: quote.soles_params || {},
        export_config: quote.export_config || {},
      });
      toast.success("Cotización guardada");
      setDirty(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const exportFile = async (kind) => {
    try {
      const res = await api.get(`/quotes/${id}/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const fname = quote.client?.proyecto || quote.name || "cotizacion";
      a.download = kind === "pdf"
        ? `${fname}.pdf`
        : kind === "plantilla"
          ? `${fname} - plantilla.xlsx`
          : `${fname}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo exportar"); }
  };

  const tabs = useMemo(() => {
    const base = [["suministro", "Suministro", Package]];
    base.push(["dolares", "Cotización dólares", DollarSign]);
    base.push(["soles", "Precios de venta", ShoppingCart]);
    base.push(["documento", "Documento / PDF", FileText]);
    if (isAdmin) base.push(["mantenimiento", "Mantenimiento", Wrench]);
    return base;
  }, [isAdmin]);

  if (!quote || !computed) {
    return <Layout><div className="text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando cotización...</div></Layout>;
  }

  return (
    <Layout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button onClick={() => nav("/")} data-testid="back-btn" className="text-slate-400 hover:text-slate-900 shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <input data-testid="quote-name" value={quote.client?.proyecto || ""} onChange={(e) => setClient("proyecto", e.target.value)}
            placeholder="Nombre del proyecto"
            className="font-display text-2xl font-extrabold text-slate-900 bg-transparent border-b-2 border-transparent hover:border-slate-200 focus:border-emerald-500 focus:outline-none min-w-0 flex-1 max-w-xl placeholder:text-slate-300" />
        </div>
        <div className="flex items-center gap-2">
          {recomputing && <span className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> calculando</span>}
          <Dialog open={histOpen} onOpenChange={(o) => { setHistOpen(o); if (o) loadVersions(); }}>
            <DialogTrigger asChild>
              <button data-testid="history-btn" title="Historial de cambios"
                className="flex items-center gap-1.5 text-sm border border-slate-300 hover:border-slate-400 bg-white px-3 py-2 rounded-md transition-colors">
                <History className="h-4 w-4 text-slate-500" /> Historial
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[75vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">Historial de cambios</DialogTitle></DialogHeader>
              <div className="space-y-2 mt-1" data-testid="history-list">
                {versions.map((v) => (
                  <div key={v.version_id} className="flex items-start gap-3 border border-slate-100 rounded-md px-3 py-2">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${v.action === "created" ? "bg-emerald-100 text-emerald-600" : v.action === "restored" ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-500"}`}>
                      <UserCircle className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-800">
                        <span className="font-semibold">{v.by_name || "Usuario"}</span>{" "}
                        {v.action === "created" ? "creó" : v.action === "restored" ? "restauró" : v.action === "prices_updated" ? "actualizó precios del catálogo" : v.action === "imported" ? "cargó desde respaldo" : "modificó"}
                        {v.role === "admin" ? "" : " (usuario)"}
                      </p>
                      <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                        <Clock className="h-3 w-3" /> {v.at ? new Date(v.at).toLocaleString("es-PE") : ""}
                        {" · "}{v.total_doors} puertas
                      </p>
                    </div>
                    {isAdmin && (
                      <button data-testid={`restore-${v.version_id}`} onClick={() => restore(v.version_id)}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-medium shrink-0 mt-0.5">
                        Restaurar
                      </button>
                    )}
                  </div>
                ))}
                {versions.length === 0 && <p className="text-sm text-slate-400">Sin registros todavía.</p>}
              </div>
            </DialogContent>
          </Dialog>
          <button data-testid="export-pdf" onClick={() => exportFile("pdf")}
            className="flex items-center gap-1.5 text-sm border border-slate-300 hover:border-slate-400 bg-white px-3 py-2 rounded-md transition-colors">
            <FileText className="h-4 w-4 text-red-500" /> PDF
          </button>
          <button data-testid="export-excel" onClick={() => exportFile("excel")}
            className="flex items-center gap-1.5 text-sm border border-slate-300 hover:border-slate-400 bg-white px-3 py-2 rounded-md transition-colors">
            <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Excel
          </button>
          {isAdmin && (
            <button data-testid="export-plantilla" onClick={() => exportFile("plantilla")}
              title="Exporta el Excel con el formato y fórmulas originales, cargado con los datos"
              className="flex items-center gap-1.5 text-sm border border-emerald-300 hover:border-emerald-400 bg-emerald-50 text-emerald-700 font-medium px-3 py-2 rounded-md transition-colors">
              <FileSpreadsheet className="h-4 w-4" /> Excel (plantilla)
            </button>
          )}
          <button data-testid="save-btn" onClick={save} disabled={saving}
            className="flex items-center gap-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 rounded-md transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar
          </button>
        </div>
      </div>

      {priceDiff?.changes?.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 mb-6 flex flex-wrap items-center gap-3" data-testid="editor-price-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1 min-w-[200px] text-sm text-amber-900">
            <span className="font-semibold">Precios del catálogo cambiados</span> en {priceDiff.changes.length} característica(s) · {priceDiff.doors.length} puerta(s) afectada(s), resaltadas en Suministro.
          </div>
          <button data-testid="editor-price-view" onClick={() => setDiffOpen(true)}
            className="flex items-center gap-1.5 text-sm font-semibold bg-white border border-amber-300 hover:bg-amber-100 text-amber-800 px-3 py-1.5 rounded-md transition-colors">
            <Eye className="h-4 w-4" /> Ver cambios{isAdmin ? " y actualizar" : ""}
          </button>
        </div>
      )}
      <PriceDiffDialog quoteId={id} open={diffOpen} onOpenChange={setDiffOpen} isAdmin={isAdmin}
        onUpdated={onPricesUpdated} unsavedWarning={dirty} />

      {/* Encabezado: datos del cliente */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {CLIENT_FIELDS.map(([k, label]) => (
          <label key={k} className="block">
            <span className="text-[11px] text-slate-500">{label}</span>
            <input data-testid={`client-${k}`} value={quote.client?.[k] || ""} onChange={(e) => setClient(k, e.target.value)}
              className="w-full mt-1 px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" />
          </label>
        ))}
      </div>

      {/* Resumen (contenedores / % incremento / total puertas) */}
      <SummaryCard summary={summary} importParams={quote.import_params} onImport={onImport}
        isAdmin={isAdmin} costTotals={isAdmin ? computed.costos?.totals : null} />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
        {tabs.map(([key, label, Icon]) => (
          <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === key ? "border-emerald-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "suministro" && (
        <SuministroTab doors={quote.doors || []} characteristics={characteristics} showPrices={isAdmin} changedDoors={changedDoors}
          onDoor={onDoor} onDoorPatch={onDoorPatch} onAddDoor={onAddDoor} onRemoveDoor={onRemoveDoor} onDuplicateDoor={onDuplicateDoor} />
      )}
      {tab === "dolares" && (
        <DolaresTab isAdmin={isAdmin} dollarParams={quote.dollar_params || {}}
          lines={isAdmin ? computed.dolares.lines : (quote.doors || []).map((d) => {
            const l = (quote.door_lines || []).find((x) => x.door_id === d.id) || {};
            return { door_id: d.id, code: d.code, qty: d.qty, utilidad: l.utilidad ?? 0.32,
              instalacion_base: l.instalacion_base ?? 0, maquillaje: l.maquillaje ?? 0,
              otros: l.otros ?? 0, cerradura: l.cerradura ?? 0 };
          })}
          totals={isAdmin ? computed.dolares.totals : null} onDollar={onDollar} onLine={onLine} onLineAll={onLineAll} references={references} />
      )}
      {tab === "mantenimiento" && isAdmin && (
        <MantenimientoTab importParams={quote.import_params} onImport={onImport}
          solesParams={quote.soles_params || {}} onSolesParam={onSolesParam}
          onCatalogSaved={() => api.get("/characteristics").then(({ data }) => setCharacteristics(data)).catch(() => {})} />
      )}
      {tab === "soles" && (
        <SolesTab soles={computed.soles} editable={true} params={quote.soles_params || {}} onParam={onSolesParam}
          isAdmin={isAdmin} dollarParams={quote.dollar_params || {}} onDollar={onDollar} references={references}
          sections={(quote.export_config || {}).sections} onSectionToggle={onSectionToggle}
          instalacionDesc={(quote.export_config || {}).instalacion_desc || ""} onInstalacionDesc={(v) => onExport("instalacion_desc", v)}
          onDoorInst={(did, v) => onDoorPatch(did, { instalacion_desc: v })} />
      )}
      {tab === "documento" && (
        <DocumentTab config={quote.export_config || {}} onExport={onExport} />
      )}
    </Layout>
  );
}
